/**
 * Очередь автосохранения черновика публикации: один `PATCH` в полёте, правки во время запроса копятся и
 * уходят следующим. Без React — `patch` и реакции внедряются, правила проверяет `node --test`.
 *
 * Отказы делятся по последствиям: `STALE` — черновик правили в другой вкладке (очередь встаёт до «перечитать»);
 * прочий 4xx — сервер это тело не примет никогда (архивный аккаунт, битая рамка), в очередь оно НЕ
 * возвращается, иначе отравит все следующие сохранения; сеть и 5xx — тело возвращается и уйдёт повтором.
 * Тип правки — параметр `E` с операциями `EditOps` (по умолчанию — правки публикации `postDraft`): той же
 * очередью сохраняются комментарии видео-референса (`videoRef.noteOps`).
 * Импорт — с расширением.
 */
import { ApiFailure } from "./api.ts";
import { isEmpty, mergeEdits, noEdits, type Edits } from "./postDraft.ts";

export type SaveFailure = "stale" | "reject" | "retry";

export function classify(error: unknown): SaveFailure {
  if (error instanceof ApiFailure) {
    if (error.code === "STALE") return "stale";
    if (error.status >= 400 && error.status < 500) return "reject";
  }
  return "retry";
}

/** Пустая правка, проверка на пустоту и склейка «`b` поверх `a`» — всё, что очереди нужно знать о правке. */
export interface EditOps<E> {
  empty: () => E;
  isEmpty: (e: E) => boolean;
  merge: (a: E, b: E) => E;
}

const POST_EDITS: EditOps<Edits> = { empty: noEdits, isEmpty, merge: mergeEdits };

export interface SaveQueueHooks<T, E = Edits> {
  /** Отправить тело; `base_modify` подставляет вызывающий (из последнего ответа). */
  patch: (body: E) => Promise<T>;
  onSaved: (result: T, sent: E) => void;
  onFailed: (error: unknown, kind: SaveFailure, sent: E) => void;
  onSaving?: (saving: boolean) => void;
  /** Можно ли слать (есть база для `base_modify`); нет — `flush` ничего не отправляет. */
  ready?: () => boolean;
}

export class SaveQueue<T, E = Edits> {
  private hooks: SaveQueueHooks<T, E>;
  private ops: EditOps<E>;
  private unsent: E;
  private inflight: Promise<void> | null = null;
  private staleNow = false;

  constructor(hooks: SaveQueueHooks<T, E>, ops: EditOps<E> = POST_EDITS as unknown as EditOps<E>) {
    this.hooks = hooks;
    this.ops = ops;
    this.unsent = ops.empty();
  }

  /** Есть неотправленное или запрос в полёте. */
  get pending(): boolean {
    return !this.ops.isEmpty(this.unsent) || this.inflight !== null;
  }

  get stale(): boolean {
    return this.staleNow;
  }

  /** Неотправленное (для проверок). */
  get queued(): E {
    return this.unsent;
  }

  add(edit: E): void {
    this.unsent = this.ops.merge(this.unsent, edit);
  }

  /** `STALE` пришёл не от PATCH (взять вариант, утвердить…): сохранение и `drain` отказывают до `reset`. */
  markStale(): void {
    this.staleNow = true;
  }

  /** «Перечитать»: очередь пуста, `STALE` снят. */
  reset(): void {
    this.unsent = this.ops.empty();
    this.staleNow = false;
  }

  /** Отправить накопленное (после запроса в полёте). Отказ — наружу. */
  flush(): Promise<void> {
    if (this.staleNow) return Promise.reject(new Error("черновик правили в другой вкладке"));
    if (this.inflight) return this.inflight.then(() => this.flush());
    if (this.ops.isEmpty(this.unsent) || this.hooks.ready?.() === false) return Promise.resolve();
    const p: Promise<void> = this.send().finally(() => {
      if (this.inflight === p) this.inflight = null;
    });
    this.inflight = p;
    return p;
  }

  /** Дождаться, пока всё сохранено (перед генерацией, утверждением, «взять вариант»). */
  async drain(): Promise<void> {
    for (;;) {
      // без базы flush ничего не шлёт — цикл бы не кончился
      if (this.pending && this.hooks.ready?.() === false) throw new Error("публикация ещё не загружена");
      await this.flush();
      if (!this.pending) return;
    }
  }

  private async send(): Promise<void> {
    const sent = this.unsent;
    this.unsent = this.ops.empty();
    this.hooks.onSaving?.(true);
    try {
      const result = await this.hooks.patch(sent);
      this.hooks.onSaved(result, sent);
    } catch (error) {
      const kind = classify(error);
      if (kind === "stale") this.staleNow = true;
      // повтор — под более свежие правки; 4xx — тело выброшено, остальная очередь цела
      else if (kind === "retry") this.unsent = this.ops.merge(sent, this.unsent);
      this.hooks.onFailed(error, kind, sent);
      throw error;
    } finally {
      this.hooks.onSaving?.(false);
    }
  }
}
